import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";

type RouteGateProps = {
  mode: "intake" | "dashboard";
  children: React.ReactNode;
};

type AccessState = "checking" | "paid" | "free";
type DashboardMode = "full" | "teaser" | "blocked";

const LS_EMAIL = "niyyah_email";
const LS_INTAKE_COMPLETE = "niyyah_intake_complete";

function isPaidStatus(subscription_status: string | null): boolean {
  return subscription_status === "trialing" || subscription_status === "active";
}

// If user is trying to upgrade / pay, don't bounce them around
function isUpgradeFlow(search: string): boolean {
  const params = new URLSearchParams(search);
  const step = (params.get("step") || "").toLowerCase();
  const plan = (params.get("plan") || "").toLowerCase();

  if (step === "payment" || step === "checkout" || step === "upgrade") return true;
  if (plan === "active" || plan === "basic") return true;

  return false;
}

// ✅ Keep ONLY demo param when we auto-redirect between intake/dashboard.
// This prevents step=intake from leaking into /dashboard?step=intake loops.
function keepDemoParam(search: string): string {
  const params = new URLSearchParams(search);
  const demo = params.get("demo");
  return demo ? `?demo=${encodeURIComponent(demo)}` : "";
}

export function RouteGate({ mode, children }: RouteGateProps) {
  const nav = useNavigate();
  const location = useLocation();
  const appEnabled = String(import.meta.env.VITE_APP_ENABLED).toLowerCase() === "true";


  const [access, setAccess] = useState<AccessState>("checking");
  const [memberFound, setMemberFound] = useState<boolean>(false);

  const [sessionEmail, setSessionEmail] = useState<string>("");
  const [authUserId, setAuthUserId] = useState<string>("");

  // ✅ NEW: server truth for intakeComplete (null = unknown)
  const [serverIntake, setServerIntake] = useState<boolean | null>(null);

  // ✅ Intake completion source of truth (DB), with LS fallback
  // null means "unknown yet"
  const [intakeComplete, setIntakeComplete] = useState<boolean | null>(() => {
    if (typeof window === "undefined") return false;
    const v = localStorage.getItem(LS_INTAKE_COMPLETE);
    if (v === "1") return true;
    if (v === "0") return false;
    return null;
  });

  // LocalStorage email is legacy/fallback only
  const lsEmail =
    typeof window !== "undefined"
      ? (localStorage.getItem(LS_EMAIL) || "").trim().toLowerCase()
      : "";

  const effectiveEmail = (sessionEmail || lsEmail).trim().toLowerCase();

  // 0) Track Supabase session (email + user id)
  useEffect(() => {
    let cancelled = false;

    const sync = async () => {
      const { data } = await supabase.auth.getSession();
      const email = (data.session?.user?.email || "").trim().toLowerCase();
      const uid = data.session?.user?.id || "";

      if (cancelled) return;

      setSessionEmail(email);
      setAuthUserId(uid);

      if (email) {
        try {
          localStorage.setItem(LS_EMAIL, email);
        } catch {}
      }
    };

    sync();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      const email = (session?.user?.email || "").trim().toLowerCase();
      const uid = session?.user?.id || "";

      setSessionEmail(email);
      setAuthUserId(uid);

      if (email) {
        try {
          localStorage.setItem(LS_EMAIL, email);
        } catch {}
      }
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  // 0b) ✅ Compute intakeComplete from DB (cross-device safe)
  // Tries members.auth_user_id first, falls back to members.email
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      // ✅ NEW: server truth wins; never let this DB effect override it
      if (serverIntake !== null) return;

      const lsDone =
        typeof window !== "undefined" &&
        localStorage.getItem(LS_INTAKE_COMPLETE) === "1";

      // If no session, rely on LS only
      if (!authUserId && !sessionEmail) {
        setIntakeComplete(lsDone);
        return;
      }

      try {
        let memberId: string | null = null;

        // 1) Find member by auth_user_id (preferred)
        if (authUserId) {
          const { data: m1, error: e1 } = await supabase
            .from("members")
            .select("id")
            .eq("auth_user_id", authUserId)
            .maybeSingle();

          if (e1) throw e1;
          if (m1?.id) memberId = m1.id;
        }

        // 2) Fallback: find member by email (case-insensitive)
        // Also fetch auth_user_id so we can "claim" it if needed.
        let emailMember: { id: string; auth_user_id: string | null } | null = null;

        if (!memberId && sessionEmail) {
          const { data: m2, error: e2 } = await supabase
            .from("members")
            .select("id, auth_user_id")
            .ilike("email", sessionEmail.trim())
            .maybeSingle();

          if (e2) throw e2;
          if (m2?.id) {
            memberId = m2.id;
            emailMember = m2 as any;
          }
        }

        // 2b) Claim: if we found the member by email and it isn't attached yet,
        // attach auth_user_id to the existing members row.
        if (memberId && authUserId && emailMember && emailMember.auth_user_id !== authUserId) {
          const { error: updErr } = await supabase
            .from("members")
            .update({ auth_user_id: authUserId })
            .eq("id", memberId);

          // Don't throw; even if this fails due to RLS, the intake check can still work.
          if (updErr) console.warn("members claim failed:", updErr.message);
        }

        if (!memberId) {
          if (!cancelled) {
            setIntakeComplete(false);
            try {
              localStorage.setItem(LS_INTAKE_COMPLETE, "0");
            } catch {}
          }
          return;
        }

        // 3) Check if an intake exists for this member_id
        const { data: intakeRow, error: intakeErr } = await supabase
          .from("intakes")
          .select("id")
          .eq("member_id", memberId)
          .limit(1)
          .maybeSingle();

        if (intakeErr) throw intakeErr;

        const done = !!intakeRow?.id;
        const effectiveDone = lsDone ? true : done;

        if (!cancelled) {
          setIntakeComplete(effectiveDone);
          try {
            localStorage.setItem(LS_INTAKE_COMPLETE, effectiveDone ? "1" : "0");
          } catch {}
        }
      } catch {
        if (!cancelled) setIntakeComplete(lsDone);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [authUserId, sessionEmail, serverIntake]);

  // 1) Determine access (demo override OR real fetch)
  useEffect(() => {
    const run = async () => {
      setAccess("checking");

      const params = new URLSearchParams(location.search);
      const demo = params.get("demo"); // "paid" | "free" | null

      // --- DEMO OVERRIDE ---
      if (demo === "paid") {
        setMemberFound(true);
        setAccess("paid");
        return;
      }
      if (demo === "free") {
        setMemberFound(true);
        setAccess("free");
        return;
      }

      // ✅ Require login for dashboard routes (dashboard + settings)
      const { data } = await supabase.auth.getSession();
      const hasSession = !!data.session;

      if (mode === "dashboard" && !hasSession) {
        nav(`/login?next=${encodeURIComponent(location.pathname + location.search)}`, {
          replace: true,
        });
        return;
      }

      if (!effectiveEmail) {
        setMemberFound(false);
        setAccess("free");
        return;
      }

      const statusUrl = import.meta.env.VITE_MEMBER_STATUS_URL as string | undefined;
      if (!statusUrl) {
        setMemberFound(true);
        setAccess("free");
        return;
      }

      try {
        const anon = (import.meta.env.VITE_SUPABASE_ANON_KEY as string) || "";
        const res = await fetch(statusUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(anon ? { Authorization: `Bearer ${anon}`, apikey: anon } : {}),
          },
          body: JSON.stringify({ email: effectiveEmail }),
        });

        const json = await res.json().catch(() => ({}));

        setMemberFound(Boolean(json?.member_found));

        // ✅ Trust server truth for intake completion (avoids RLS issues client-side)
        if (typeof json?.intake_complete === "boolean") {
          setServerIntake(json.intake_complete);
          setIntakeComplete(json.intake_complete);
          try {
            localStorage.setItem(LS_INTAKE_COMPLETE, json.intake_complete ? "1" : "0");
          } catch {}
        }

        const paid = isPaidStatus(json?.subscription_status ?? null);
        setAccess(paid ? "paid" : "free");
      } catch {
        setMemberFound(true);
        setAccess("free");
      }
    };

    run();
  }, [location.pathname, location.search, effectiveEmail, mode, nav]);

  // 2) Decide dashboard mode (full/teaser/blocked)
  const dashboardMode: DashboardMode = useMemo(() => {
    if (!intakeComplete) return "blocked";
    if (access !== "paid") return "teaser";
    return "full";
  }, [access, intakeComplete]);

  // 3) Route behavior (NO query-string leakage between intake/dashboard)
  useEffect(() => {
    if (access === "checking") return;
    if (intakeComplete === null) return;

    const upgrading = isUpgradeFlow(location.search);

    // If user is trying to pay/upgrade, do NOT auto-bounce them to dashboard
    if (mode === "intake" && upgrading) return;

    // If intake complete, keep them on dashboard
    if (intakeComplete) {
      // Only auto-send to dashboard when the app is actually enabled.
      // When app is disabled (form-only launch), do NOT bounce users into /dashboard.
      if (appEnabled && mode === "intake" && location.pathname !== "/dashboard") {
        nav("/dashboard" + keepDemoParam(location.search), { replace: true });
      }
      return;
    }

    // If intake is NOT complete:
    // DO NOT redirect away from /dashboard.
    // Dashboard will render in "blocked" mode (blur + CTA).
  }, [access, intakeComplete, mode, nav, location.pathname, location.search, appEnabled]);

  // While checking access or intakeComplete, render nothing to prevent flicker/loops
  if (access === "checking" || intakeComplete === null) return null;

  // Inject "mode" only for dashboard routes
  if (mode === "dashboard" && React.isValidElement(children)) {
    const childProps = (children as any).props || {};
    if ("mode" in childProps || location.pathname === "/dashboard") {
      return React.cloneElement(children as any, { mode: dashboardMode });
    }
    return children;
  }

  return <>{children}</>;
}